/**
 * Stakeholder Pulse types
 *
 * Stakeholder Pulse closes the loop with idea submitters after prioritization
 * outcomes are published. Each pulse record represents one notification +
 * optional response for a (item, submitter, priority_set) tuple.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/**
 * Where the item landed relative to the cut line in the published priority list.
 * `not_in_list` means the item was not included in this particular priority list.
 */
export type PulseOutcome =
  | 'above_cut_line'
  | 'below_cut_line'
  | 'not_in_list';

/**
 * Lifecycle of the pulse notification.
 */
export type PulseStatus = 'pending' | 'sent' | 'viewed' | 'responded';

/**
 * Submitter's answer to "Did we address your concern?"
 */
export type PulseResponse =
  | 'addressed'
  | 'partially_addressed'
  | 'not_addressed';

// ── Core type ────────────────────────────────────────────────────────────────

export interface StakeholderPulse {
  id: string;
  workspaceId: string;

  /** The submitted idea that was prioritized */
  itemId: string;

  /** The submitter to notify — may be null if the item has no linked submitter */
  submitterId: string | null;

  /** The published priority list that triggered this pulse */
  prioritySetId: string;

  /** Where the item landed in the list */
  outcome: PulseOutcome;

  /** Rank in the priority list (null when outcome = 'not_in_list') */
  rank: number | null;

  /** Human-readable summary delivered to the submitter */
  outcomeSummary: string;

  /** Notification lifecycle */
  status: PulseStatus;

  sentAt: Date | null;
  viewedAt: Date | null;
  respondedAt: Date | null;

  /** Submitter's response */
  response: PulseResponse | null;

  /** Optional free-text from the submitter */
  responseNote: string | null;

  createdAt: Date;
  createdBy: string | null;
  updatedAt: Date;
}

// ── View-model enriched for UI ───────────────────────────────────────────────

export interface StakeholderPulseWithDetails extends StakeholderPulse {
  itemTitle?: string;
  submitterName?: string;
  prioritySetName?: string;
}

// ── DB row mapping helpers ───────────────────────────────────────────────────

export function rowToStakeholderPulse(row: Record<string, unknown>): StakeholderPulse {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    itemId: row.item_id as string,
    submitterId: (row.submitter_id as string) ?? null,
    prioritySetId: row.priority_set_id as string,
    outcome: row.outcome as PulseOutcome,
    rank: row.rank != null ? Number(row.rank) : null,
    outcomeSummary: (row.outcome_summary as string) ?? '',
    status: (row.status as PulseStatus) ?? 'pending',
    sentAt: row.sent_at ? new Date(row.sent_at as string) : null,
    viewedAt: row.viewed_at ? new Date(row.viewed_at as string) : null,
    respondedAt: row.responded_at ? new Date(row.responded_at as string) : null,
    response: (row.response as PulseResponse) ?? null,
    responseNote: (row.response_note as string) ?? null,
    createdAt: new Date(row.created_at as string),
    createdBy: (row.created_by as string) ?? null,
    updatedAt: new Date(row.updated_at as string),
  };
}

export function stakeholderPulseToRow(
  pulse: Partial<StakeholderPulse>,
): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (pulse.workspaceId !== undefined) row.workspace_id = pulse.workspaceId;
  if (pulse.itemId !== undefined) row.item_id = pulse.itemId;
  if (pulse.submitterId !== undefined) row.submitter_id = pulse.submitterId;
  if (pulse.prioritySetId !== undefined) row.priority_set_id = pulse.prioritySetId;
  if (pulse.outcome !== undefined) row.outcome = pulse.outcome;
  if (pulse.rank !== undefined) row.rank = pulse.rank;
  if (pulse.outcomeSummary !== undefined) row.outcome_summary = pulse.outcomeSummary;
  if (pulse.status !== undefined) row.status = pulse.status;
  if (pulse.sentAt !== undefined) row.sent_at = pulse.sentAt?.toISOString() ?? null;
  if (pulse.viewedAt !== undefined) row.viewed_at = pulse.viewedAt?.toISOString() ?? null;
  if (pulse.respondedAt !== undefined) row.responded_at = pulse.respondedAt?.toISOString() ?? null;
  if (pulse.response !== undefined) row.response = pulse.response;
  if (pulse.responseNote !== undefined) row.response_note = pulse.responseNote;
  if (pulse.createdBy !== undefined) row.created_by = pulse.createdBy;
  return row;
}

// ── Label helpers ────────────────────────────────────────────────────────────

export const PULSE_OUTCOME_LABELS: Record<PulseOutcome, string> = {
  above_cut_line: 'Committed',
  below_cut_line: 'Ranked — below cut line',
  not_in_list: 'Not included',
};

export const PULSE_RESPONSE_LABELS: Record<PulseResponse, string> = {
  addressed: 'Yes, addressed',
  partially_addressed: 'Partially addressed',
  not_addressed: 'Not addressed',
};

export const PULSE_STATUS_LABELS: Record<PulseStatus, string> = {
  pending: 'Pending',
  sent: 'Sent',
  viewed: 'Viewed',
  responded: 'Responded',
};
